import {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useMemo,
  type ChangeEvent,
  type FocusEvent,
  type KeyboardEvent,
  type MouseEvent as ReactMouseEvent,
} from 'react';
import { AccessTime } from '@mui/icons-material';
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Paper,
  Popper,
  TextField,
  type SxProps,
  type Theme,
} from '@mui/material';

export interface TimePickerProps {
  value?: string;
  onChange?: (time: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  minuteStep?: number;
  usePortal?: boolean;
  sx?: SxProps<Theme>;
}

function normalizeTimeInput(
  raw: string,
  options?: { allowHourOnly?: boolean }
): string | null {
  const value = raw.trim();
  if (!value) return null;

  const colonMatch = value.match(/^(\d{1,2}):(\d{1,2})$/);
  if (colonMatch) {
    const hour = Number(colonMatch[1]);
    const minute = Number(colonMatch[2]);
    if (
      Number.isFinite(hour) &&
      Number.isFinite(minute) &&
      hour >= 0 &&
      hour <= 23 &&
      minute >= 0 &&
      minute <= 59
    ) {
      return `${String(hour).padStart(2, '0')}:${String(minute).padStart(
        2,
        '0'
      )}`;
    }
    return null;
  }

  if (/^\d{4}$/.test(value)) {
    const hour = Number(value.slice(0, 2));
    const minute = Number(value.slice(2, 4));
    if (hour >= 0 && hour <= 23 && minute >= 0 && minute <= 59) {
      return `${String(hour).padStart(2, '0')}:${String(minute).padStart(
        2,
        '0'
      )}`;
    }
  }

  if (/^\d{3}$/.test(value)) {
    const hour = Number(value.slice(0, 1));
    const minute = Number(value.slice(1, 3));
    if (hour >= 0 && hour <= 23 && minute >= 0 && minute <= 59) {
      return `${String(hour).padStart(2, '0')}:${String(minute).padStart(
        2,
        '0'
      )}`;
    }
  }

  if (options?.allowHourOnly && /^\d{1,2}$/.test(value)) {
    const hour = Number(value);
    if (hour >= 0 && hour <= 23) {
      return `${String(hour).padStart(2, '0')}:00`;
    }
  }

  return null;
}

const columnScrollSx: SxProps<Theme> = {
  height: 192,
  overflowY: 'auto',
  scrollBehavior: 'smooth',
  p: 0.5,
  width: '50%',
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': { display: 'none' },
};

export function TimePicker({
  value = '',
  onChange,
  placeholder = '09:00',
  disabled = false,
  className = '',
  minuteStep = 5,
  usePortal = true,
  sx,
}: TimePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [typedValue, setTypedValue] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const paperRef = useRef<HTMLDivElement>(null);
  const hourScrollRef = useRef<HTMLDivElement>(null);
  const minuteScrollRef = useRef<HTMLDivElement>(null);

  const displayValue = typedValue !== null ? typedValue : value;

  const { hour: selectedHour, minute: selectedMinute } = useMemo(() => {
    if (!displayValue || !displayValue.includes(':'))
      return { hour: '00', minute: '00' };
    const [h, m] = displayValue.split(':');
    return {
      hour: h?.padStart(2, '0') || '00',
      minute: m?.padStart(2, '0') || '00',
    };
  }, [displayValue]);

  useLayoutEffect(() => {
    if (!isOpen) return;
    const alignScroll = (container: HTMLDivElement | null, val: string) => {
      if (!container) return;
      const target = container.querySelector(
        `[data-value="${val}"]`
      ) as HTMLElement;
      if (target) {
        container.scrollTop = target.offsetTop - container.offsetTop - 8;
      }
    };
    alignScroll(hourScrollRef.current, selectedHour);
    alignScroll(minuteScrollRef.current, selectedMinute);
  }, [isOpen, selectedHour, selectedMinute]);

  useEffect(() => {
    const handleKeyDown = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
        setTypedValue(null);
      }
    };
    const handleMouseDown = (e: globalThis.MouseEvent) => {
      const t = e.target as Node;
      if (
        rootRef.current?.contains(t) ||
        paperRef.current?.contains(t)
      ) {
        return;
      }
      setIsOpen(false);
      setTypedValue(null);
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleMouseDown);
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const hours = Array.from({ length: 24 }, (_, i) =>
    i.toString().padStart(2, '0')
  );

  const minutes = useMemo(() => {
    const step = minuteStep || 5;
    const length = Math.ceil(60 / step);
    return Array.from({ length }, (_, i) =>
      (i * step).toString().padStart(2, '0')
    );
  }, [minuteStep]);

  const updateTime = (h: string, m: string) => {
    const formattedTime = `${h}:${m}`;
    setTypedValue(null);
    onChange?.(formattedTime);
  };

  const validateAndChange = (val: string) => {
    setTypedValue(val);
    const normalized = normalizeTimeInput(val);
    if (normalized) {
      onChange?.(normalized);
    }
  };

  const commitTypedTime = (raw: string) => {
    const normalized = normalizeTimeInput(raw, { allowHourOnly: true });
    if (!normalized) return;
    onChange?.(normalized);
    setTypedValue(null);
    setIsOpen(false);
  };

  const openPicker = () => {
    if (disabled) return;
    const el = inputRef.current;
    if (!el) return;
    setAnchorEl(el);
    setIsOpen(true);
    el.focus();
  };

  return (
    <Box
      ref={rootRef}
      className={className}
      sx={{ position: 'relative', width: '100%', ...sx }}
    >
      <TextField
        size="small"
        fullWidth
        value={displayValue}
        required={false}
        inputRef={inputRef}
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          validateAndChange(e.target.value)
        }
        onFocus={(e: FocusEvent<HTMLInputElement>) => {
          setAnchorEl(e.currentTarget);
          setIsOpen(true);
        }}
        onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
          if (event.key !== 'Enter' || event.repeat) return;
          if (event.nativeEvent.isComposing) return;
          event.preventDefault();
          event.stopPropagation();
          commitTypedTime(event.currentTarget.value);
        }}
        onBlur={() => {
          if (typedValue !== null) {
            const normalized = normalizeTimeInput(typedValue, {
              allowHourOnly: true,
            });
            if (normalized) {
              onChange?.(normalized);
            }
          }
          setTypedValue(null);
        }}
        placeholder={placeholder}
        disabled={disabled}
        inputProps={{
          className: 'tabular-nums',
          style: { textAlign: 'center' },
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                type="button"
                size="small"
                edge="end"
                disabled={disabled}
                tabIndex={-1}
                aria-label="시간 선택"
                onMouseDown={(e: ReactMouseEvent) => e.preventDefault()}
                onClick={() => openPicker()}
                sx={{ mr: -0.5 }}
              >
                <AccessTime sx={{ color: 'text.secondary', fontSize: 20 }} />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      <Popper
        open={isOpen && !disabled && Boolean(anchorEl)}
        anchorEl={anchorEl}
        placement="bottom"
        disablePortal={!usePortal}
        style={{ zIndex: 1300 }}
      >
        <Paper
          ref={paperRef}
          elevation={8}
          sx={{
            mt: 0.5,
            p: 1,
            display: 'flex',
            gap: 0.5,
            minWidth: 140,
            borderRadius: 2,
            border: (theme: Theme) => `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box ref={hourScrollRef} sx={columnScrollSx}>
            {hours.map(h => (
              <TimeButton
                key={h}
                val={h}
                selected={selectedHour === h}
                onClick={() => updateTime(h, selectedMinute)}
              />
            ))}
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              color: 'grey.300',
              fontWeight: 700,
              flexShrink: 0,
              pt: 0.5,
            }}
          >
            :
          </Box>
          <Box ref={minuteScrollRef} sx={columnScrollSx}>
            {minutes.map(m => (
              <TimeButton
                key={m}
                val={m}
                selected={selectedMinute === m}
                onClick={() => updateTime(selectedHour, m)}
              />
            ))}
          </Box>
        </Paper>
      </Popper>
    </Box>
  );
}

interface TimeButtonProps {
  val: string;
  selected: boolean;
  onClick: () => void;
}

function TimeButton({ val, selected, onClick }: TimeButtonProps) {
  return (
    <Button
      type="button"
      data-value={val}
      onMouseDown={(e: ReactMouseEvent) => e.preventDefault()}
      onClick={onClick}
      disableElevation
      sx={{
        width: '100%',
        py: 0.75,
        mb: 0.5,
        minWidth: 0,
        fontSize: 14,
        textTransform: 'none',
        color: selected ? 'common.white' : 'text.secondary',
        bgcolor: selected ? 'grey.900' : 'transparent',
        '&:hover': {
          bgcolor: selected ? 'grey.800' : 'grey.100',
        },
      }}
    >
      {val}
    </Button>
  );
}

export default TimePicker;
